<script setup>
import { computed } from 'vue';

const props = defineProps({
  sellers: { type: Array, default: () => [] },
  title: { type: String, default: 'Vendas por Vendedor' },
});

// Badges acionaveis: destaca o lider em cada dimensao (so quando ha base pra isso).
const badges = computed(() => {
  const map = {};
  const add = (id, label) => {
    if (id == null) return;
    (map[id] = map[id] || []).push(label);
  };
  const pick = (filterFn, betterFn) => {
    const pool = props.sellers.filter(filterFn);
    if (!pool.length) return null;
    return pool.reduce((a, b) => (betterFn(b, a) ? b : a));
  };
  const top = pick(
    s => Number(s.revenue || 0) > 0,
    (b, a) => b.revenue > a.revenue
  );
  if (top) add(top.agentId, '🏆 Top vendas');
  const conv = pick(
    s => Number(s.won || 0) > 0,
    (b, a) => b.conversionRate > a.conversionRate
  );
  if (conv) add(conv.agentId, '🎯 Melhor conversão');
  const cycle = pick(
    s => Number(s.avgCycleDays || 0) > 0,
    (b, a) => b.avgCycleDays < a.avgCycleDays
  );
  if (cycle) add(cycle.agentId, '⚡ Ciclo mais rápido');
  return map;
});

const medal = index => ['🥇', '🥈', '🥉'][index] || '';

const formatCurrency = value => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value || 0);
};

const formatCycle = days => {
  const value = Number(days || 0);
  if (!value) return '—';
  return value === 1 ? '1 dia' : `${value} dias`;
};
</script>

<template>
  <div class="rounded-2xl border border-n-weak bg-n-solid-1 p-3">
    <h3 class="text-sm font-extrabold text-n-slate-12 mb-2">
      {{ title }}
    </h3>
    <div v-if="sellers.length === 0" class="text-sm text-n-slate-10 py-3 text-center">
      Nenhuma venda encontrada no período
    </div>
    <div v-else class="overflow-x-auto">
      <table class="w-full text-sm">
        <thead>
          <tr class="border-b border-n-weak">
            <th class="text-left py-2 px-3 text-[11.5px] font-bold uppercase tracking-wide text-n-slate-10">#</th>
            <th class="text-left py-2 px-3 text-[11.5px] font-bold uppercase tracking-wide text-n-slate-10">Vendedor</th>
            <th class="text-center py-2 px-3 text-[11.5px] font-bold uppercase tracking-wide text-n-slate-10">Oportunidades</th>
            <th class="text-center py-2 px-3 text-[11.5px] font-bold uppercase tracking-wide text-n-slate-10">Ganhas</th>
            <th class="text-center py-2 px-3 text-[11.5px] font-bold uppercase tracking-wide text-n-slate-10">Perdidas</th>
            <th class="text-center py-2 px-3 text-[11.5px] font-bold uppercase tracking-wide text-n-slate-10">Conversão</th>
            <th class="text-right py-2 px-3 text-[11.5px] font-bold uppercase tracking-wide text-n-slate-10">Valor Fechado</th>
            <th class="text-right py-2 px-3 text-[11.5px] font-bold uppercase tracking-wide text-n-slate-10">Ticket Médio</th>
            <th class="text-center py-2 px-3 text-[11.5px] font-bold uppercase tracking-wide text-n-slate-10">Ciclo Médio</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(seller, index) in sellers"
            :key="seller.agentId"
            class="border-b border-n-weak hover:bg-n-alpha-2"
          >
            <td class="py-2.5 px-3 text-n-slate-10 font-medium tabular-nums whitespace-nowrap">
              <span v-if="medal(index)" class="text-base">{{ medal(index) }}</span>
              <span v-else>{{ index + 1 }}</span>
            </td>
            <td class="py-2.5 px-3 text-n-slate-12 font-medium">
              <div class="flex flex-col gap-1">
                <span>{{ seller.name }}</span>
                <div
                  v-if="(badges[seller.agentId] || []).length"
                  class="flex flex-wrap gap-1"
                >
                  <span
                    v-for="badge in badges[seller.agentId]"
                    :key="badge"
                    class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-n-alpha-2 text-n-slate-11"
                  >
                    {{ badge }}
                  </span>
                </div>
              </div>
            </td>
            <td class="py-2.5 px-3 text-center text-n-slate-11">
              {{ seller.deals }}
            </td>
            <td class="py-2.5 px-3 text-center">
              <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                {{ seller.won }}
              </span>
            </td>
            <td class="py-2.5 px-3 text-center">
              <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                {{ seller.lost }}
              </span>
            </td>
            <td class="py-2.5 px-3 text-center">
              <span
                class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                :class="seller.conversionRate >= 50
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400'"
              >
                {{ seller.conversionRate }}%
              </span>
            </td>
            <td class="py-2.5 px-3 text-right text-n-slate-12 font-medium">
              {{ formatCurrency(seller.revenue) }}
            </td>
            <td class="py-2.5 px-3 text-right text-n-slate-11">
              {{ formatCurrency(seller.avgTicket) }}
            </td>
            <td class="py-2.5 px-3 text-center text-n-slate-11">
              {{ formatCycle(seller.avgCycleDays) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
